<script setup lang="ts">
import TimeEntryDeleteDialog from '@/components/dialogs/TimeEntryDeleteDialog.vue';
import TimeEntryDialog from '@/components/dialogs/TimeEntryDialog.vue';
import WorkingPeriodsExportDialog from '@/components/dialogs/WorkingPeriodsExportDialog.vue';
import { useAccordionScroll } from '@/composables/useAccordionScroll';
import { useEmployee } from '@/composables/useEmployee';
import { useManager } from '@/composables/useManager';
import { useUserStore } from '@/stores/useUserStore';
import type { Employee } from '@/types/api';
import type { WorkingPeriod } from '@/types/employee';
import { formatRestaurantDateTime, formatTime, getCurrentRestaurantTime } from '@/utils/time';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import Button from 'primevue/button';
import Divider from 'primevue/divider';
import Menu from 'primevue/menu';
import Skeleton from 'primevue/skeleton';
import { useToast } from 'primevue/usetoast';
import type { ComponentPublicInstance } from 'vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

dayjs.extend(utc);
dayjs.extend(timezone);

const route = useRoute();
const toast = useToast();
const { t, locale } = useI18n();
const { getPaginatedWorkingPeriods } = useEmployee();
const { getTeamMemberByRestaurantUserId } = useManager();
const userStore = useUserStore();
const user = computed(() => userStore.user);

// Get restaurant user ID from route params
const restaurantUserId = computed(() => Number(route.params.restaurantUserId));

// Employee data
const worker = ref<Employee | null>(null);
const loadingWorker = ref(false);

// State
const loading = ref(false);
const loadingMore = ref(false);
const workingPeriods = ref<WorkingPeriod[]>([]);
const hasMorePages = ref(false);
const nextCursor = ref<string | null>(null); // Cursor for loading more records
const PER_PAGE = 15; // Load 15 records per request
// const dateBounds = ref<{ earliest_date: string | null; latest_date: string | null; has_entries: boolean } | null>(null);

// Accordion scroll-into-view logic
const { activeIndex: workingPeriodsActiveIndex, setHeaderRef: setWorkingPeriodsHeaderRef } = useAccordionScroll<number[]>([]);

// Dialog states
const showCreateDialog = ref(false);
const showEditDialog = ref(false);
const showDeleteDialog = ref(false);
const showExportDialog = ref(false);
const selectedPeriod = ref<WorkingPeriod | null>(null);
const presetDate = ref<Date | null>(null); // New state for preset date

// Menu references - Fix the type
const periodMenus = ref<{ [key: number]: ComponentPublicInstance & { toggle: (event: Event) => void } }>({});

// Get restaurant timezone from user's active restaurant
const restaurantTimezone = computed(() => {
  return user.value?.active_restaurant?.timezone || 'UTC';
});

// Check if current user can edit working periods
const canEditWorkingPeriods = computed(() => {
  const currentUser = user.value;
  const activeRestaurant = currentUser?.active_restaurant;

  if (!activeRestaurant) return false;

  // Admins and managers can always edit
  if (activeRestaurant.role === 'admin' || activeRestaurant.role === 'manager') {
    return true;
  }

  // For other roles, check if the restaurant allows employee edits
  return activeRestaurant.allow_employee_working_period_edits === true;
});

// Computed properties
const workingPeriodGroupedByDay = computed(() => {
  const groups: Record<string, { periods: WorkingPeriod[]; date: Date }> = {};

  workingPeriods.value.forEach((period) => {
    // Use the restaurant_date from backend if available, otherwise fallback to timezone conversion
    let dateKey: string;
    let groupDate: Date;

    if (period.restaurant_date) {
      // Use the pre-calculated restaurant date from backend
      const restaurantDate = new Date(period.restaurant_date + 'T00:00:00');
      dateKey = restaurantDate.toLocaleDateString(locale.value, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      groupDate = restaurantDate;
    } else {
      // Fallback to timezone conversion
      const startTime = dayjs.utc(period.start_time).tz(restaurantTimezone.value);
      dateKey = startTime.toDate().toLocaleDateString(locale.value, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      groupDate = startTime.startOf('day').toDate();
    }

    // Capitalize the first letter of the date string
    const capitalizedDateKey = dateKey.charAt(0).toUpperCase() + dateKey.slice(1);

    if (!groups[capitalizedDateKey]) {
      groups[capitalizedDateKey] = {
        periods: [],
        date: groupDate,
      };
    }
    groups[capitalizedDateKey].periods.push(period);
  });

  // Sort groups by date (newest first)
  const sortedGroups: Record<string, { periods: WorkingPeriod[]; date: Date }> = {};
  Object.keys(groups)
    .sort((a, b) => {
      return groups[b].date.getTime() - groups[a].date.getTime();
    })
    .forEach((key) => {
      // Sort periods within each day by start time (restaurant timezone)
      groups[key].periods.sort((a, b) => {
        const timeA = dayjs.utc(a.start_time).tz(restaurantTimezone.value);
        const timeB = dayjs.utc(b.start_time).tz(restaurantTimezone.value);
        return timeA.valueOf() - timeB.valueOf();
      });
      sortedGroups[key] = groups[key];
    });

  return sortedGroups;
});

// Calculate total duration for a day in seconds
const getDayWorkingDuration = (dayPeriods: WorkingPeriod[]): number => {
  return dayPeriods.reduce((total, period) => {
    // Use the backend-calculated duration_minutes
    if (period.duration_minutes) {
      return total + period.duration_minutes * 60; // Convert minutes to seconds
    }
    return total;
  }, 0);
};

// Calculate duration of a single working period in seconds
const getWorkingPeriodDurationInSec = (period: WorkingPeriod): number => {
  // For ongoing periods, calculate duration in real-time using restaurant timezone
  if (!period.end_time && period.start_time) {
    const start = dayjs.utc(period.start_time);
    const now = getCurrentRestaurantTime(restaurantTimezone.value).utc();
    return now.diff(start, 'second');
  }

  // Use the backend-calculated duration_minutes for completed periods
  if (period.duration_minutes) {
    return period.duration_minutes * 60; // Convert minutes to seconds
  }
  return 0;
};

// Load working periods using record-based pagination
const loadWorkingPeriods = async (append: boolean = false) => {
  if (!append) {
    loading.value = true;
    // Reset cursor when loading fresh
    nextCursor.value = null;
  } else {
    loadingMore.value = true;
  }

  try {
    const { data, error } = await getPaginatedWorkingPeriods(restaurantUserId.value, {
      mode: 'records',
      per_page: PER_PAGE,
      before_date: append ? (nextCursor.value ?? undefined) : undefined,
    });

    if (error) {
      console.error('Error loading working periods:', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('employee.error.errorFetchingWorkingPeriods'),
        life: 3000,
      });
      return;
    }

    if (data) {
      if (append) {
        // Append new periods to existing ones, avoiding duplicates
        const existingIds = new Set(workingPeriods.value.map((p) => p.id));
        const newPeriods = data.working_periods.filter((p) => !existingIds.has(p.id));
        workingPeriods.value = [...workingPeriods.value, ...newPeriods];
      } else {
        // Replace existing periods
        workingPeriods.value = data.working_periods;
      }

      // Update cursor and pagination state
      nextCursor.value = data.pagination.next_cursor ?? null;
      hasMorePages.value = data.pagination.has_more_pages;

      // Store date bounds for potential "jump to" functionality
      // if (data.date_bounds) {
      //   dateBounds.value = data.date_bounds;
      // }
    }
  } catch (err) {
    console.error('Error loading working periods:', err);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.error.errorFetchingWorkingPeriods'),
      life: 3000,
    });
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

// Load more periods
const loadMore = async () => {
  if (!hasMorePages.value || loadingMore.value) return;
  await loadWorkingPeriods(true);
};

// Format time for display in restaurant timezone
const formatPeriodTime = (dateString: string): string => {
  return formatRestaurantDateTime(dateString, 'HH:mm', restaurantTimezone.value);
};

// Dialog methods
const openCreateDialog = (dateContext?: Date) => {
  selectedPeriod.value = null;
  presetDate.value = dateContext || null;
  showCreateDialog.value = true;
};

const openEditDialog = (period: WorkingPeriod) => {
  selectedPeriod.value = period;
  presetDate.value = null;
  showEditDialog.value = true;
};

const openDeleteDialog = (period: WorkingPeriod) => {
  selectedPeriod.value = period;
  showDeleteDialog.value = true;
};

const handlePeriodSaved = (_savedPeriod: WorkingPeriod) => {
  // Refresh the data to get updated periods (fresh load without cursor)
  loadWorkingPeriods(false);
};

const handlePeriodDeleted = (periodId: number) => {
  // Remove the deleted period from local state
  workingPeriods.value = workingPeriods.value.filter((period) => period.id !== periodId);
};

// Period menu items
const getPeriodMenuItems = (period: WorkingPeriod) => [
  {
    label: t('common.edit'),
    icon: 'pi pi-pencil',
    command: () => openEditDialog(period),
  },
  {
    label: t('common.delete'),
    icon: 'pi pi-trash',
    command: () => openDeleteDialog(period),
    class: 'text-red-500',
  },
];

const togglePeriodMenu = (event: Event, period: WorkingPeriod) => {
  periodMenus.value[period.id]?.toggle(event);
};

// Fetch worker details
const fetchWorkerDetails = async () => {
  if (!user.value?.active_restaurant) return;

  loadingWorker.value = true;
  try {
    const { data, error: fetchError } = await getTeamMemberByRestaurantUserId(user.value.active_restaurant.id, restaurantUserId.value);

    if (fetchError || !data) {
      console.error('Error fetching worker details:', fetchError);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('employee.error.errorFetchingUserDetails'),
        life: 3000,
      });
      return;
    }

    worker.value = data.user;
  } catch (err) {
    console.error('Error fetching worker details:', err);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.error.errorFetchingUserDetails'),
      life: 3000,
    });
  } finally {
    loadingWorker.value = false;
  }
};

// Export hours functionality
const exportHours = () => {
  if (!worker.value) return;
  showExportDialog.value = true;
};

// Initialize data on mount
onMounted(() => {
  loadWorkingPeriods();
  fetchWorkerDetails();
});
</script>

<template>
  <main>
    <div class="flex flex-col gap-4">
      <div class="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 v-if="loadingWorker">{{ t('employee.detail.loading') }}</h2>
          <h2 v-else-if="worker">{{ worker.first_name }} {{ worker.last_name }}</h2>
          <h2 v-else>{{ t('employee.timeclock.timeEntries') }}</h2>
        </div>
        <!-- Only show add entry button if user can edit working periods -->
        <Button v-if="canEditWorkingPeriods" :label="t('employee.timeclock.addEntry')" icon="pi pi-plus" size="small" @click="openCreateDialog()" />
      </div>

      <!-- Work time information -->
      <div v-if="worker && !loadingWorker" class="flex flex-col items-start gap-2">
        <h3>{{ t('employee.timeclock.workTime') }}</h3>

        <div class="flex w-full flex-row items-center justify-between gap-2">
          <p>{{ t('employee.timeclock.today') }} :</p>
          <p>{{ formatTime(worker?.today_worked_minutes * 60) }}</p>
        </div>

        <div class="flex w-full flex-row items-center justify-between gap-2">
          <p>{{ t('employee.timeclock.thisWeek') }} :</p>
          <p>{{ formatTime(worker?.week_worked_minutes * 60) }}</p>
        </div>

        <div class="flex w-full flex-row items-center justify-between gap-2">
          <p>{{ t('employee.timeclock.thisMonth') }} :</p>
          <p>{{ formatTime(worker?.month_worked_minutes * 60) }}</p>
        </div>

        <div class="flex w-full flex-row items-center justify-between gap-2">
          <p>{{ t('employee.timeclock.lastMonth') }} :</p>
          <p>{{ formatTime(worker?.last_month_worked_minutes * 60) }}</p>
        </div>

        <Button :label="t('employee.timeclock.exportHoursExcel')" class="w-full" @click="exportHours" />
      </div>

      <!-- Work time skeleton while loading -->
      <div v-if="loadingWorker" class="flex flex-col gap-2">
        <Skeleton width="8rem" height="1.5rem" />
        <div v-for="i in 3" :key="i" class="flex flex-row items-center justify-between">
          <Skeleton width="6rem" height="1rem" />
          <Skeleton width="4rem" height="1rem" />
        </div>
        <Skeleton width="100%" height="2.5rem" />
      </div>

      <hr v-if="worker && !loadingWorker" class="my-4" />

      <!-- Loading skeleton for initial load -->
      <div v-if="loading" class="flex flex-col gap-2">
        <div v-for="i in 3" :key="i" class="border-round border-1 surface-border surface-card flex flex-row items-center justify-between p-4">
          <Skeleton width="10rem" height="1.5rem" />
          <Skeleton width="6rem" height="1.5rem" />
        </div>
      </div>

      <!-- Working periods accordion -->
      <Accordion v-else-if="Object.keys(workingPeriodGroupedByDay).length > 0" v-model:active-index="workingPeriodsActiveIndex">
        <AccordionTab v-for="(dayGroup, name, index) in workingPeriodGroupedByDay" :key="index" :pt="{ content: { class: 'flex flex-col gap-2' } }">
          <template #header>
            <div
              :ref="(el) => setWorkingPeriodsHeaderRef(el as HTMLElement | null, index as number)"
              class="flex w-full flex-row items-center justify-between"
            >
              <span class="font-bold">{{ name }}</span>
              <span class="number">
                {{ formatTime(getDayWorkingDuration(dayGroup.periods)) }}
              </span>
            </div>
          </template>

          <div v-for="period in dayGroup.periods" :key="period.id" class="flex flex-col gap-0">
            <div class="flex flex-row items-center justify-between" :class="{ 'text-blue-600': period.is_manually_edited }">
              <div class="flex flex-row items-center gap-2">
                <span class="number">
                  {{ formatPeriodTime(period.start_time) }} -
                  {{ period.end_time ? formatPeriodTime(period.end_time) : t('employee.timeclock.onGoing') }}
                </span>

                <Divider layout="vertical" />
                <span>{{ formatTime(getWorkingPeriodDurationInSec(period)) }}</span>

                <!-- Manual edit indicator -->
                <span
                  v-if="period.is_manually_edited"
                  class="pi pi-pencil text-xs text-blue-600"
                  :title="t('employee.timeclock.manuallyEdited')"
                ></span>
              </div>

              <!-- Only show menu button if user can edit working periods -->
              <div v-if="canEditWorkingPeriods" class="flex items-center gap-2">
                <Button icon="pi pi-ellipsis-v" text size="small" :aria-label="t('common.more')" @click="togglePeriodMenu($event, period)" />
                <Menu
                  :ref="
                    (el) => {
                      if (el && typeof el === 'object' && 'toggle' in el) periodMenus[period.id] = el as any;
                    }
                  "
                  :model="getPeriodMenuItems(period)"
                  popup
                />
              </div>
            </div>
            <span v-if="period.is_cross_day" class="text-xs text-orange-500">
              {{ t('employee.timeclock.crossDay') }}
            </span>
          </div>

          <!-- Only show add entry button for specific day if user can edit working periods -->
          <Button
            v-if="canEditWorkingPeriods"
            :label="`+ ${t('employee.timeclock.addEntry')}`"
            text
            class="p-0 text-left"
            @click="openCreateDialog(dayGroup.date)"
          />
        </AccordionTab>
      </Accordion>

      <!-- No data state -->
      <div v-else-if="!loading" class="p-8 text-center">
        <p class="mb-4 text-gray-500">{{ t('employee.timeclock.noTimeEntries') }}</p>
        <!-- Only show add first entry button if user can edit working periods -->
        <Button v-if="canEditWorkingPeriods" :label="t('employee.timeclock.addFirstEntry')" icon="pi pi-plus" @click="openCreateDialog()" />
      </div>

      <!-- Load more button -->
      <div v-if="hasMorePages && !loading" class="mt-4 flex justify-center">
        <Button :label="t('common.loadMore')" icon="pi pi-chevron-down" :loading="loadingMore" outlined @click="loadMore" />
      </div>
    </div>

    <!-- Dialogs - Only render if user can edit working periods -->
    <TimeEntryDialog
      v-if="canEditWorkingPeriods"
      v-model:visible="showCreateDialog"
      :restaurant-user-id="restaurantUserId"
      mode="create"
      :preset-date="presetDate"
      @saved="handlePeriodSaved"
    />

    <TimeEntryDialog
      v-if="canEditWorkingPeriods"
      v-model:visible="showEditDialog"
      :restaurant-user-id="restaurantUserId"
      :working-period="selectedPeriod"
      mode="edit"
      @saved="handlePeriodSaved"
    />

    <TimeEntryDeleteDialog
      v-if="canEditWorkingPeriods"
      v-model:visible="showDeleteDialog"
      :restaurant-user-id="restaurantUserId"
      :working-period="selectedPeriod"
      @deleted="handlePeriodDeleted"
    />

    <!-- Working Periods Export Dialog -->
    <WorkingPeriodsExportDialog v-model:visible="showExportDialog" :worker="worker" />
  </main>
</template>
